<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { LayerList, NewLayerDialog } from '@/components/layers'
import { Settings } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { HOST, API } from '@/utils/http'

const store = useStore()
const mapReady = computed(() => store.getters['geo/getMapReady'])
const map = computed(() => store.getters['geo/getMap'])
const orgs = computed(() => store.getters['org/getOrgs'])

const selectedOrgId = ref(null)

const selectedOrg = computed(() => {
  return orgs.value.find((o) => o.organization_id === parseInt(selectedOrgId.value))
})

const orgLayers = ref([])

const layerToFeature = (layer) => {
  return {
    uuid: layer.code,
    type: 'Feature',
    geometry: layer.layer?.geometry ? layer.layer.geometry : null,
    properties: {
      type: layer.type,
      visible: false,
      code: layer.code,
      name: layer.name,
      organization_id: layer.org_id,
      layer: layer.layer,
      created_at: layer.created_at,
    }
  }
}

const fetchLayers = async (org) => {
  const response = await API({
    method: 'GET',
    url: `${HOST}/layers/${org.organization_id}`
  })
  orgLayers.value = response.data.map(layerToFeature).sort((a, b) => new Date(b.properties.created_at) - new Date(a.properties.created_at))
}

const onFormSubmitted = (layer) => {
  orgLayers.value = [layerToFeature(layer), ...orgLayers.value]
}

const deleteLayer = (layerId) => {
  orgLayers.value = orgLayers.value.filter((layer) => layer.uuid !== layerId)
}

watch(
  () => selectedOrgId.value,
  async (value, oldValue) => {
    if (value || (value && value !== oldValue)) {
      localStorage.setItem('selectedOrgId', value)
      if (selectedOrg.value) {
        await fetchLayers(selectedOrg.value)
        await store.dispatch('geo/fetchTulumToken', selectedOrgId.value)
      }
    }
  }
)

watch(
  () => orgs.value,
  (value) => {
    if (value.length > 0) {
      const storedOrgId = localStorage.getItem('selectedOrgId')
      if (
        storedOrgId &&
        value.some((o) => o.organization_id === parseInt(storedOrgId))
      ) {
        selectedOrgId.value = storedOrgId
      } else {
        selectedOrgId.value = value[0].organization_id.toString()
      }
    }
  }
)
</script>

<template>
  <div v-if="map && mapReady && selectedOrg" class="border-b border-gray-300 mt-3">
    <div class="px-3 flex items-center justify-between">
      <div class="flex items-center">
        <Select v-if="orgs.length > 1" v-model="selectedOrgId">
          <SelectTrigger>
            <SelectValue placeholder="Select an organization">
              <b>{{ selectedOrg?.organization_name }}</b>
            </SelectValue>
          </SelectTrigger>
          <SelectContent class="max-h-[300px]">
            <SelectGroup>
              <SelectItem
                v-for="(o, index) of orgs"
                :key="index"
                :value="`${o.organization_id}`"
              >
                {{ o.organization_name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        <div v-else>
          <b>{{ selectedOrg?.organization_name }}</b>
        </div>

        <router-link :to="{ name: 'OrgMembers', params: { org_id: selectedOrgId }}">
          <Button class="rounded-full w-7 h-7 p-0" variant="ghost" size="sm">
            <Settings class="w-4 h-4 text-gray-500" />
          </Button>
        </router-link>
      </div>
      <div v-if="selectedOrg" :key="selectedOrg.organization_id">
        <NewLayerDialog
          :feature="{}"
          :orgs="orgs"
          :org="selectedOrg"
          @form-submitted="onFormSubmitted($event)"
        />
      </div>
    </div>

    <div
      v-if="mapReady && orgLayers?.length > 0 && selectedOrg"
      :key="selectedOrg.organization_id + orgLayers?.length"
    >
      <LayerList :layers="orgLayers" :key="orgLayers.length" @deleteLayer="deleteLayer" />
    </div>
    <div v-else>
      <div class="p-3 text-gray-500">No layers found</div>
    </div>
  </div>
</template>
